/**
 * Whether the room can be seen (docs/refactor/25, phase 4): on screen and in a visible tab. Off screen, the room's
 * animations pause — dozens of agents bobbing in a tab nobody looks at is work for nothing.
 */

import { useEffect, useState, type RefObject } from 'react'

export function useOnScreen(ref: RefObject<Element>): boolean {
  const [inView, setInView] = useState(true)
  const [tabVisible, setTabVisible] = useState(() => typeof document === 'undefined' || document.visibilityState !== 'hidden')

  useEffect(() => {
    const element = ref.current
    if (!element || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver((entries) => setInView(entries.some((entry) => entry.isIntersecting)))
    observer.observe(element)
    return () => observer.disconnect()
  }, [ref])

  useEffect(() => {
    const update = () => setTabVisible(document.visibilityState !== 'hidden')
    document.addEventListener('visibilitychange', update)
    return () => document.removeEventListener('visibilitychange', update)
  }, [])

  return inView && tabVisible
}
